/**
 * useWebPlacements Hook
 *
 * React hook for managing web placements with CRUD operations.
 * Provides list, create, update, and delete functionality.
 *
 * Usage:
 * ```tsx
 * function WebPlacementsPage({ projectId }: Props) {
 *   const {
 *     webPlacements,
 *     isLoading,
 *     error,
 *     create,
 *     update,
 *     remove,
 *     refresh
 *   } = useWebPlacements(projectId);
 *
 *   if (isLoading) return <CircularProgress />;
 *   if (error) return <Alert severity="error">{error.message}</Alert>;
 *
 *   return (
 *     <List>
 *       {webPlacements.map(wp => (
 *         <ListItem key={wp.id}>{wp.name}</ListItem>
 *       ))}
 *     </List>
 *   );
 * }
 * ```
 *
 * @module webplacements/hooks
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { WebPlacementService } from '@modules/firebase-core/services/WebPlacementService';
import type {
  WebPlacement,
  CreateWebPlacementInput,
  UpdateWebPlacementInput,
} from '@modules/webplacements/types/webplacements.types';

/**
 * Result type for useWebPlacements hook
 */
export interface UseWebPlacementsResult {
  /** List of web placements */
  webPlacements: WebPlacement[];

  /** Whether data is loading */
  isLoading: boolean;

  /** Error from last operation */
  error: Error | null;

  /** Refresh the list from Firestore */
  refresh: () => Promise<void>;

  /** Create a new web placement */
  create: (data: Omit<CreateWebPlacementInput, 'createdBy'>, userId: string) => Promise<WebPlacement>;

  /** Update an existing web placement */
  update: (id: string, data: UpdateWebPlacementInput) => Promise<WebPlacement>;

  /** Delete a web placement */
  remove: (id: string) => Promise<void>;

  /** Clone an existing web placement (clears onboarding-dependent fields) */
  clone: (sourceId: string, userId: string) => Promise<WebPlacement>;

  /** Full-copy an existing web placement (preserves onboarding-dependent fields) */
  cloneFull: (sourceId: string, userId: string) => Promise<WebPlacement>;

  /** Check if alias is globally unique */
  isAliasUnique: (alias: string, excludeId?: string) => Promise<boolean>;

  /** Clear error state */
  clearError: () => void;
}

/**
 * useWebPlacements Hook
 *
 * @param projectId - Project ID to load web placements for
 * @returns Web placements list and CRUD operations
 */
export function useWebPlacements(projectId: string): UseWebPlacementsResult {
  // State
  const [webPlacements, setWebPlacements] = useState<WebPlacement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Service ref (stable across renders)
  const serviceRef = useRef<WebPlacementService | null>(null);

  // Get or create service instance
  const getService = useCallback(() => {
    if (!serviceRef.current) {
      serviceRef.current = new WebPlacementService();
    }
    return serviceRef.current;
  }, []);

  // Load data on mount and when projectId changes
  const refresh = useCallback(async () => {
    if (!projectId) {
      setWebPlacements([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const service = getService();
      const list = await service.list(projectId);
      setWebPlacements(list);
    } catch (e) {
      const loadError = e instanceof Error ? e : new Error(String(e));
      setError(loadError);
      console.error('[useWebPlacements] Failed to load:', loadError);
    } finally {
      setIsLoading(false);
    }
  }, [projectId, getService]);

  // Initial load
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Create operation
  const create = useCallback(
    async (data: Omit<CreateWebPlacementInput, 'createdBy'>, userId: string): Promise<WebPlacement> => {
      setError(null);

      try {
        const service = getService();
        const created = await service.create(projectId, {
          ...data,
          createdBy: userId,
        });

        // Add to local state
        setWebPlacements((prev) => [created, ...prev]);

        return created;
      } catch (e) {
        const createError = e instanceof Error ? e : new Error(String(e));
        setError(createError);
        throw createError;
      }
    },
    [projectId, getService]
  );

  // Update operation
  const update = useCallback(
    async (id: string, data: UpdateWebPlacementInput): Promise<WebPlacement> => {
      setError(null);

      try {
        const service = getService();
        const updated = await service.update(projectId, id, data);

        // Update in local state
        setWebPlacements((prev) =>
          prev.map((wp) => (wp.id === id ? updated : wp))
        );

        return updated;
      } catch (e) {
        const updateError = e instanceof Error ? e : new Error(String(e));
        setError(updateError);
        throw updateError;
      }
    },
    [projectId, getService]
  );

  // Delete operation
  const remove = useCallback(
    async (id: string): Promise<void> => {
      setError(null);

      try {
        const service = getService();
        await service.delete(projectId, id);

        // Remove from local state
        setWebPlacements((prev) => prev.filter((wp) => wp.id !== id));
      } catch (e) {
        const deleteError = e instanceof Error ? e : new Error(String(e));
        setError(deleteError);
        throw deleteError;
      }
    },
    [projectId, getService]
  );

  // Clone operation
  const clone = useCallback(
    async (sourceId: string, userId: string): Promise<WebPlacement> => {
      setError(null);

      try {
        const service = getService();
        const cloned = await service.clone(projectId, sourceId, userId);

        // Prepend to local state
        setWebPlacements((prev) => [cloned, ...prev]);

        return cloned;
      } catch (e) {
        const cloneError = e instanceof Error ? e : new Error(String(e));
        setError(cloneError);
        throw cloneError;
      }
    },
    [projectId, getService]
  );

  // Full clone operation
  const cloneFull = useCallback(
    async (sourceId: string, userId: string): Promise<WebPlacement> => {
      setError(null);

      try {
        const service = getService();
        const cloned = await service.cloneFull(projectId, sourceId, userId);

        setWebPlacements((prev) => [cloned, ...prev]);

        return cloned;
      } catch (e) {
        const cloneError = e instanceof Error ? e : new Error(String(e));
        setError(cloneError);
        throw cloneError;
      }
    },
    [projectId, getService]
  );

  // Alias uniqueness check (project-scoped)
  const isAliasUnique = useCallback(
    async (alias: string, excludeId?: string): Promise<boolean> => {
      try {
        const service = getService();
        return await service.isAliasUnique(projectId, alias, excludeId);
      } catch (e) {
        // Log the error for debugging
        console.error('[useWebPlacements] isAliasUnique failed:', e);

        // Re-throw the error so the UI can show the real problem
        // instead of silently treating failures as "alias in use"
        const message = e instanceof Error ? e.message : 'Failed to check alias uniqueness';
        throw new Error(`Unable to verify alias: ${message}`);
      }
    },
    [projectId, getService]
  );

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    webPlacements,
    isLoading,
    error,
    refresh,
    create,
    update,
    remove,
    clone,
    cloneFull,
    isAliasUnique,
    clearError,
  };
}
